import { Link, useRouterState } from '@tanstack/react-router'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import type { ReactNode } from 'react'
import { Logo } from '@/components/logo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useUiStore } from '@/stores/ui'
import type { NavItem, NavSection } from '../nav'

const EXACT = new Set(['/', '/admin'])

export function isNavActive(pathname: string, to: string): boolean {
  if (EXACT.has(to)) return pathname === to || pathname === `${to}/`
  return pathname === to || pathname.startsWith(`${to}/`)
}

function NavLink({ item, collapsed, onNavigate }: { item: NavItem; collapsed: boolean; onNavigate?: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const active = isNavActive(pathname, item.to)
  const Icon = item.icon
  const link = (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex h-9 items-center gap-3 rounded-lg px-2.5 text-[13.5px] font-medium text-sidebar-foreground/85 transition-colors outline-none hover:bg-sidebar-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
        active && 'bg-sidebar-accent text-foreground',
        collapsed && 'justify-center px-0',
      )}
    >
      {active && <span className="absolute top-1/2 -left-3 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-gradient" aria-hidden />}
      <Icon className={cn('size-[18px] shrink-0 transition-colors', active ? 'text-primary' : 'text-sidebar-muted group-hover:text-foreground')} strokeWidth={1.85} />
      {!collapsed && (
        <>
          <span className="truncate">{item.label}</span>
          {item.upcoming && (
            <Badge variant="outline" className="ml-auto border-dashed px-1.5 py-0 text-[10px] font-normal text-sidebar-muted">
              Soon
            </Badge>
          )}
        </>
      )}
    </Link>
  )
  if (!collapsed) return link
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  )
}

export function SidebarNav({ sections, collapsed = false, onNavigate }: { sections: NavSection[]; collapsed?: boolean; onNavigate?: () => void }) {
  return (
    <nav aria-label="Main" className="flex flex-col gap-5 px-3">
      {sections.map((section, i) => (
        <div key={section.label ?? i} className="flex flex-col gap-0.5">
          {section.label &&
            (collapsed ? (
              <div className="mx-auto mb-1 h-px w-6 bg-sidebar-border" aria-hidden />
            ) : (
              <div className="px-2.5 pb-1.5 text-[10.5px] font-semibold tracking-[0.08em] text-sidebar-muted uppercase">{section.label}</div>
            ))}
          {section.items.filter((item) => !item.hidden).map((item) => (
            <NavLink key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
    </nav>
  )
}

export function Sidebar({ sections, footer, badge }: { sections: NavSection[]; footer?: (collapsed: boolean) => ReactNode; badge?: ReactNode }) {
  const collapsed = useUiStore((s) => s.sidebarCollapsed)
  const toggle = useUiStore((s) => s.toggleSidebar)
  return (
    <aside
      className={cn(
        'sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200 ease-out lg:flex',
        collapsed ? 'w-[68px]' : 'w-[252px]',
      )}
    >
      <div className={cn('flex h-14 shrink-0 items-center', collapsed ? 'justify-center px-2' : 'justify-between px-4')}>
        <Link to="/" className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Signetix home">
          <Logo collapsed={collapsed} />
        </Link>
        {!collapsed && badge}
      </div>
      <div className="flex-1 overflow-y-auto py-3 scrollbar-thin">
        <SidebarNav sections={sections} collapsed={collapsed} />
      </div>
      {footer?.(collapsed)}
      <div className={cn('flex shrink-0 border-t border-sidebar-border p-2', collapsed ? 'justify-center' : 'justify-end')}>
        <Button variant="ghost" size="icon-sm" onClick={toggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} className="text-sidebar-muted">
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </Button>
      </div>
    </aside>
  )
}
